<template>
  <section class="admin-content" id="procurement-order-details-page">
    <Navbar />
    <main class="admin-main">

      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3">
                <i class="fe fe-file-text" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Order Details</h4>
                <small style="opacity: .8;" v-if="order">
                  {{ order.order_number }}
                  <span class="mx-1">·</span>
                  <span :class="'order-badge order-badge--' + (order.status || 'pending')">{{ statusLabel(order.status) }}</span>
                  <span class="mx-1">·</span>
                  Payment: <strong>{{ order.payment_status }}</strong>
                  <span class="mx-1">·</span>
                  {{ order.channel === 'marketplace' ? 'Marketplace order' : 'Agency procurement' }}
                </small>
              </div>
            </div>
            <button type="button" class="btn btn-outline-light btn-sm" @click="$router.back()">
              <i class="fe fe-arrow-left mr-1"></i>Back
            </button>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;" v-if="order">
        <div class="container pb-5">
          <div class="row">

            <!-- Left column -->
            <div class="col-md-8 mb-4">

              <!-- Parties -->
              <div class="card shadow-sm table-card mb-4">
                <div class="row px-4 py-3">
                  <div class="col-sm-6 mb-2">
                    <div class="small text-muted">Vendor</div>
                    <div class="font-weight-bold">{{ order.vendor ? order.vendor.name : order.vendor_id }}</div>
                    <div class="small text-muted" v-if="order.vendor">{{ order.vendor.phone_number }} {{ order.vendor.email }}</div>
                  </div>
                  <div class="col-sm-6 mb-2">
                    <div class="small text-muted">Ordered by</div>
                    <div class="font-weight-bold">{{ buyerName }}</div>
                    <div class="small text-muted">{{ order.requested_at || order.created_at }}</div>
                  </div>
                  <div class="col-12" v-if="order.notes">
                    <div class="small text-muted">Notes</div>
                    <div style="white-space: pre-line; font-size: .9rem;">{{ order.notes }}</div>
                  </div>
                </div>
              </div>

              <!-- Items -->
              <div class="card shadow-sm table-card mb-4">
                <div class="d-flex align-items-center px-4 pt-4 pb-3">
                  <i class="fe fe-package text-primary mr-2"></i>
                  <h6 class="mb-0 font-weight-bold">Order Items</h6>
                  <span class="badge badge-soft-info ml-2">{{ (order.items || []).length }}</span>
                </div>
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">Item</th>
                        <th class="table-th">Requested</th>
                        <th class="table-th">Approved</th>
                        <th class="table-th">Unit Price</th>
                        <th class="table-th">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in order.items" :key="item.id" class="table-row">
                        <td class="px-4 py-3 font-weight-bold" style="font-size:.88rem;">
                          {{ itemName(item) }}
                        </td>
                        <td class="py-3">{{ item.requested_quantity }}</td>
                        <td class="py-3 text-success font-weight-bold">{{ item.approved_quantity }}</td>
                        <td class="py-3"><i class="mdi mdi-currency-ngn"></i>{{ item.unit_price }}</td>
                        <td class="py-3">
                          <span :class="'order-badge order-badge--' + (item.status || 'pending')">{{ item.status }}</span>
                        </td>
                      </tr>
                      <tr v-if="!order.items || !order.items.length">
                        <td colspan="5" class="text-center py-4 text-muted">No items</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Comments -->
              <div class="card shadow-sm form-card mb-4">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-message-circle" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Comments</h6>
                  <span class="badge badge-light ml-auto">{{ (order.comments || []).length }}</span>
                </div>
                <div class="card-body px-4 py-4">
                  <div class="comment-item" v-for="comment in order.comments" :key="comment.id">
                    <div class="comment-meta"><i class="fe fe-user mr-1"></i>#{{ comment.id }}</div>
                    <div class="comment-body">{{ comment.body }}</div>
                  </div>
                  <div v-if="!order.comments || !order.comments.length" class="text-muted small mb-3">No comments yet.</div>
                  <textarea v-model="commentBody" class="form-control styled-input" rows="3" placeholder="Add a comment…"></textarea>
                  <button type="button" class="btn btn-submit mt-3" @click="addComment">
                    <i class="fe fe-send mr-1"></i>Post Comment
                  </button>
                </div>
              </div>

              <!-- Payments -->
              <div class="card shadow-sm table-card">
                <div class="d-flex align-items-center px-4 pt-4 pb-3">
                  <i class="fe fe-credit-card text-primary mr-2"></i>
                  <h6 class="mb-0 font-weight-bold">Payments</h6>
                  <span class="badge badge-soft-info ml-2">{{ (order.payments || []).length }}</span>
                </div>
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">Amount</th>
                        <th class="table-th">Mode</th>
                        <th class="table-th">Status</th>
                        <th class="table-th">Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="p in order.payments" :key="p.id" class="table-row">
                        <td class="px-4 py-3 font-weight-bold"><i class="mdi mdi-currency-ngn"></i>{{ p.amount }}</td>
                        <td class="py-3">{{ p.payment_mode }}</td>
                        <td class="py-3">
                          <span :class="'pay-badge pay-badge--' + (p.status || 'pending')">{{ p.status }}</span>
                        </td>
                        <td class="py-3 text-muted small">{{ p.reference || '—' }}</td>
                      </tr>
                      <tr v-if="!order.payments || !order.payments.length">
                        <td colspan="4" class="text-center py-4 text-muted">No payments recorded</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            <!-- Right column -->
            <div class="col-md-4">

              <!-- Vendor Review -->
              <div class="card shadow-sm form-card mb-4" v-if="isVendor && order.status === 'submitted'">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-check-square" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Vendor Review</h6>
                </div>
                <div class="card-body px-4 py-4">
                  <div class="review-item" v-for="review in reviewItems" :key="review.id">
                    <div class="small font-weight-bold mb-2">{{ review.name }} <span class="text-muted font-weight-normal">· requested {{ review.requested }}</span></div>
                    <div class="form-group">
                      <label class="form-label-sm">Approved Qty</label>
                      <input v-model.number="review.approved_quantity" class="form-control form-control-sm styled-input" type="number" min="0"
                        :max="review.requested" :aria-label="'Approved quantity for ' + review.name" />
                    </div>
                    <div class="form-group mb-0">
                      <label class="form-label-sm">Unit Price</label>
                      <div class="input-group input-group-sm">
                        <div class="input-group-prepend"><span class="input-group-text"><i class="mdi mdi-currency-ngn"></i></span></div>
                        <input v-model.number="review.unit_price" class="form-control styled-input" type="number" min="0" step="0.01"
                          :aria-label="'Unit price for ' + review.name" />
                      </div>
                    </div>
                  </div>
                  <p class="small text-muted mt-2 mb-0">Set a quantity to 0 to decline an item. Approving takes the quantity out of your stock.</p>
                  <button type="button" class="btn btn-submit btn-block mt-3" @click="submitVendorApproval">
                    <i class="fe fe-check mr-1"></i>Submit Approval
                  </button>
                </div>
              </div>

              <!-- Vendor dispatch -->
              <div class="card shadow-sm form-card mb-4" v-if="isVendor && ['approved', 'partially_approved'].includes(order.status)">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-truck" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Dispatch</h6>
                </div>
                <div class="card-body px-4 py-4">
                  <p class="text-muted small mb-3">Mark the approved goods as sent to the facility.</p>
                  <button type="button" class="btn btn-submit btn-block" @click="dispatchOrder">
                    <i class="fe fe-truck mr-1"></i>Mark as dispatched
                  </button>
                </div>
              </div>

              <!-- Buyer cancel -->
              <div class="card shadow-sm form-card mb-4" v-if="isBuyer && order.status === 'submitted'">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-x-circle" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Cancel order</h6>
                </div>
                <div class="card-body px-4 py-4">
                  <p class="text-muted small mb-3">You can cancel until the vendor reviews the order.</p>
                  <button type="button" class="btn btn-outline-danger btn-block" @click="cancelOrder">
                    <i class="fe fe-x mr-1"></i>Cancel order
                  </button>
                </div>
              </div>

              <!-- Provider Receipt -->
              <div class="card shadow-sm form-card mb-4" v-if="isBuyer && ['approved', 'partially_approved', 'dispatched'].includes(order.status)">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-package" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Provider Receipt</h6>
                </div>
                <div class="card-body px-4 py-4">
                  <p class="text-muted small mb-3">Confirm that the goods ordered have been physically received.</p>
                  <button type="button" class="btn btn-success btn-block" @click="confirmReceived">
                    <i class="fe fe-check-circle mr-1"></i>Confirm Goods Received
                  </button>
                </div>
              </div>

              <!-- Add Payment -->
              <div class="card shadow-sm form-card" v-if="canPay">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-dollar-sign" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Add Payment</h6>
                </div>
                <div class="card-body px-4 py-4">
                  <p class="small text-muted">
                    Approved total: <strong><i class="mdi mdi-currency-ngn"></i>{{ order.total_approved_amount }}</strong>.
                    Record payments made outside Claimify (transfer, cheque, cash).
                  </p>
                  <div class="form-group">
                    <label class="form-label-sm">Amount</label>
                    <div class="input-group">
                      <div class="input-group-prepend"><span class="input-group-text"><i class="mdi mdi-currency-ngn"></i></span></div>
                      <input v-model.number="payment.amount" class="form-control styled-input" type="number" min="0" step="0.01" />
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="form-label-sm">Mode</label>
                    <select v-model="payment.payment_mode" class="form-control styled-input">
                      <option value="offline">Offline</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label class="form-label-sm">Status</label>
                    <select v-model="payment.status" class="form-control styled-input">
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label class="form-label-sm">Reference</label>
                    <input v-model="payment.reference" class="form-control styled-input" type="text" placeholder="Transaction reference…" />
                  </div>
                  <button type="button" class="btn btn-submit btn-block" @click="addPayment">
                    <i class="fe fe-save mr-1"></i>Record Payment
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

const STATUS_LABELS = { partially_approved: "partially approved" };

export default {
  components: { Navbar },
  data() {
    let user = {};
    try {
      user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch (e) {
      user = {};
    }
    return {
      user,
      order: null,
      reviewItems: [],
      commentBody: "",
      payment: { amount: "", payment_mode: "offline", status: "completed", reference: "" },
    };
  },
  computed: {
    isVendor() {
      return this.user.type === "vendor_staff" || this.user.type === "vendor_employee";
    },
    isBuyer() {
      return this.user.type === "provider" || this.user.type === "provider_employee";
    },
    canPay() {
      return this.order && ["approved", "partially_approved", "dispatched", "received"].includes(this.order.status);
    },
    buyerName() {
      const u = this.order && this.order.provider_user;
      if (!u) return this.order ? "#" + this.order.provider_user_id : "";
      return u.agency_name || `${u.firstname || ""} ${u.lastname || ""}`.trim();
    },
  },
  beforeMount() {
    this.fetchOrder();
  },
  methods: {
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    itemName(item) {
      if (item.product_name) return item.product_name;
      if (item.product) return item.product.name;
      return item.drug ? item.drug.drug_name : "#" + (item.drug_id || item.id);
    },
    dispatchOrder() {
      this.axios
        .post(`/api/v1/auth/procurement-orders/${this.$route.params.id}/dispatch`)
        .then(() => {
          this.$breadstick.notify("Order marked as dispatched", { position: "top-right" });
          this.fetchOrder();
        })
        .catch((err) => {
          this.$breadstick.notify(err.response?.data?.message || "Dispatch failed", { position: "top-right" });
        });
    },
    cancelOrder() {
      if (!window.confirm("Cancel this order?")) return;
      this.axios
        .post(`/api/v1/auth/procurement-orders/${this.$route.params.id}/cancel`)
        .then(() => {
          this.$breadstick.notify("Order cancelled", { position: "top-right" });
          this.fetchOrder();
        })
        .catch((err) => {
          this.$breadstick.notify(err.response?.data?.message || "Cancel failed", { position: "top-right" });
        });
    },
    fetchOrder() {
      this.axios.get(`/api/v1/auth/procurement-orders/${this.$route.params.id}`).then((res) => {
        this.order = res.data.data;
        this.reviewItems = (this.order.items || []).map((item) => ({
          id: item.id,
          name: this.itemName(item),
          requested: item.requested_quantity,
          // Pre-filled with the requested quantity while the order awaits review.
          approved_quantity: this.order.status === "submitted" ? item.requested_quantity : item.approved_quantity || 0,
          unit_price: item.unit_price || 0,
        }));
      });
    },
    submitVendorApproval() {
      this.axios
        .post(`/api/v1/auth/procurement-orders/${this.$route.params.id}/vendor-approve`, { items: this.reviewItems })
        .then(() => {
          this.$breadstick.notify("Vendor approval submitted", { position: "top-right" });
          this.fetchOrder();
        })
        .catch((err) => {
          this.$breadstick.notify(err.response?.data?.message || "Approval failed", { position: "top-right" });
        });
    },
    confirmReceived() {
      this.axios
        .post(`/api/v1/auth/procurement-orders/${this.$route.params.id}/confirm-received`)
        .then(() => {
          this.$breadstick.notify("Goods receipt confirmed", { position: "top-right" });
          this.fetchOrder();
        })
        .catch((err) => {
          this.$breadstick.notify(err.response?.data?.message || "Receipt failed", { position: "top-right" });
        });
    },
    addComment() {
      this.axios
        .post(`/api/v1/auth/procurement-orders/${this.$route.params.id}/comments`, { body: this.commentBody })
        .then(() => {
          this.commentBody = "";
          this.fetchOrder();
        });
    },
    addPayment() {
      this.axios
        .post(`/api/v1/auth/procurement-orders/${this.$route.params.id}/payments`, this.payment)
        .then(() => {
          this.$breadstick.notify("Payment recorded", { position: "top-right" });
          this.payment = { amount: "", payment_mode: "offline", status: "completed", reference: "" };
          this.fetchOrder();
        })
        .catch((err) => {
          this.$breadstick.notify(err.response?.data?.message || "Payment failed", { position: "top-right" });
        });
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); }
.header-icon-wrap {
  width: 48px; height: 48px; background: rgba(255,255,255,0.15);
  border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

/* Form card */
.form-card { border-radius: 14px; overflow: hidden; border: none; }
.form-card-header {
  display: flex; align-items: center; gap: 12px; padding: 14px 24px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}
.section-icon {
  width: 28px; height: 28px; background: rgba(255,255,255,.2);
  border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0;
}
.form-label-sm { font-weight: 600; font-size: .8rem; color: #374151; margin-bottom: 4px; display: block; }
.styled-input { border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: .88rem; }
.styled-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }

.review-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; margin-bottom: 10px; }

/* Comments */
.comment-item { border-left: 3px solid #2563eb; padding: 8px 12px; margin-bottom: 10px; background: #f8fafc; border-radius: 0 8px 8px 0; }
.comment-meta { font-size: .75rem; color: #94a3b8; margin-bottom: 4px; }
.comment-body { font-size: .88rem; color: #374151; }

/* Table card */
.table-card { border-radius: 12px; border: none; }
.table-th {
  font-size: .73rem; text-transform: uppercase; letter-spacing: .04em;
  color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px;
}
.table-row:hover { background: #f8fafc; }

/* Badges */
.order-badge, .pay-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: .75rem; font-weight: 600; text-transform: capitalize;
}
.order-badge--submitted  { background: #dbeafe; color: #1d4ed8; }
.order-badge--approved   { background: #d1fae5; color: #065f46; }
.order-badge--received   { background: #d1fae5; color: #065f46; }
.order-badge--pending    { background: #fef3c7; color: #92400e; }
.order-badge--rejected   { background: #fee2e2; color: #991b1b; }
.order-badge--cancelled  { background: #e2e8f0; color: #475569; }
.order-badge--dispatched { background: #ede9fe; color: #5b21b6; }
.order-badge--partially_approved { background: #d1fae5; color: #065f46; }
.pay-badge--completed    { background: #d1fae5; color: #065f46; }
.pay-badge--paid         { background: #d1fae5; color: #065f46; }
.pay-badge--pending      { background: #fef3c7; color: #92400e; }
.pay-badge--failed       { background: #fee2e2; color: #991b1b; }

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none; color: #fff; border-radius: 8px; font-weight: 600; padding: 8px 20px;
}
.btn-submit:hover { opacity: .9; color: #fff; }
</style>
